// hooks/useEmployeeCalendar.ts
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useAppContext } from "../../../../../../AppContext";
import { showCustomConfirmDialog } from "../../../../dialogs/AppConfirmDialog";
import { EventType } from "../../../../../enums";
import {
  IBookedSlotModel,
  ICalendarDay,
  IEmployeeWithAvailabilityModel,
  ITimeSlot,
} from "../models/employeeCalendarModels";
import { getBookingMinutesOnDate } from "../utils/bookedSlotRangeUtility";
import {
  getBookedBufferMinutes,
  getEffectiveBufferMinutes,
  toBlockedIntervals,
  toWorkingShifts,
} from "../../../../../infrastructure/utilities/serviceBufferUtility";
import { formatBufferAfter } from "../../../../../infrastructure/utilities/durationUtility";

export type SelectionMode = "slots" | "custom";

interface IUseEmployeeCalendarProps {
  employeeFieldName: string;
  dateFieldName: string;
  durationFieldName: string;
  employeesData: IEmployeeWithAvailabilityModel[];
  selectedEmployeeId?: number;
  selectedDate?: Date;
  selectedDuration?: number;
  /** The selected service's buffer after the reservation (cut at the end of the working day). */
  bufferAfterMinutes?: number;
  onClose: () => void;
}

const formatDuration = (durationInMinutes: number): string => {
  const durationMap: Record<number, string> = {
    60: "1 час",
    90: "1,5 часа",
    120: "2 часа",
    150: "2,5 часа",
    180: "3 часа",
  };

  if (durationMap[durationInMinutes]) {
    return durationMap[durationInMinutes];
  }

  if (durationInMinutes < 60) {
    return `${durationInMinutes} мин`;
  }

  const hours = Math.floor(durationInMinutes / 60);
  const minutes = durationInMinutes % 60;
  return minutes > 0
    ? `${hours}:${minutes.toString().padStart(2, "0")} ч`
    : `${hours} ч`;
};

const formatTime = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

const createDateOnly = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

// Every booking that touches `date`, with the minutes it keeps the employee busy on that
// day: the booking plus its service's buffer (`busyUntil`). A booking from the day before,
// or a full-day block, is clipped to the day.
const getBookingRangesOnDate = (bookedSlots: IBookedSlotModel[], date: Date) =>
  bookedSlots.flatMap((booking) => {
    const range = getBookingMinutesOnDate(
      { startTime: booking.startTime, endTime: booking.busyUntil || booking.endTime },
      date
    );
    return range ? [{ booking, ...range }] : [];
  });

const timeToMinutes = (timeStr: string): number => {
  const [hour, minute] = timeStr.split(":").map(Number);
  return hour * 60 + minute;
};

const generateTooltipForBusySlot = (): string => {
  return "Слотът е зает.";
};

// "1 час" or "1 час +30 мин буфер" when the service keeps the employee busy afterwards.
const describeBooking = (duration: number, bufferMinutes: number): string =>
  bufferMinutes > 0
    ? `${formatDuration(duration)} ${formatBufferAfter(bufferMinutes)} буфер`
    : formatDuration(duration);

// A booked slot's own time, "10:00-11:00", followed by its service's buffer when it keeps the
// employee busy afterwards: "10:00-11:00 +30 мин буфер".
const formatBookedTime = (booking: IBookedSlotModel): string => {
  const range = `${formatTime(new Date(booking.startTime))}-${formatTime(
    new Date(booking.endTime)
  )}`;
  const buffer = formatBufferAfter(getBookedBufferMinutes(booking));
  return buffer ? `${range} ${buffer} буфер` : range;
};

const generateTooltipForConflicts = (
  conflicts: IBookedSlotModel[],
  duration: number,
  startTime: string,
  bufferMinutes = 0
): string => {
  const durationText = describeBooking(duration, bufferMinutes);
  let tooltip = `Открити са следните конфликти за резервация с продължителност ${durationText}, от ${startTime}:\n`;

  conflicts.forEach((conflict, index) => {
    const timeText = formatBookedTime(conflict);
    const isIndOrService =
      conflict.eventType === EventType.Individual ||
      conflict.eventType === EventType.Service;

    const conflictText = isIndOrService
      ? `${index + 1}. ${conflict.serviceName} (${timeText}) с ${
          conflict.clientName
        }`
      : `${index + 1}. ${conflict.serviceName} (${timeText})`;

    tooltip += conflictText;
    if (index < conflicts.length - 1) tooltip += "\n";
  });

  return tooltip;
};

const buildCustomConflictWarning = (
  conflicts: IBookedSlotModel[],
  duration: number,
  bufferMinutes = 0
): string => {
  const durationText = describeBooking(duration, bufferMinutes);
  let message = `Конфликт за резервация (${durationText}):\n`;
  conflicts.forEach((conflict, index) => {
    const isIndOrService =
      conflict.eventType === EventType.Individual ||
      conflict.eventType === EventType.Service;

    message += isIndOrService
      ? `${index + 1}. ${conflict.serviceName} (${formatBookedTime(conflict)}) с ${
          conflict.clientName
        }`
      : `${index + 1}. ${conflict.serviceName} (${formatBookedTime(conflict)})`;
    if (index < conflicts.length - 1) message += "\n";
  });
  return message;
};

export const useEmployeeCalendar = ({
  employeeFieldName,
  dateFieldName,
  durationFieldName,
  employeesData,
  selectedEmployeeId,
  selectedDate,
  selectedDuration,
  bufferAfterMinutes,
  onClose,
}: IUseEmployeeCalendarProps) => {
  const { setValue } = useFormContext();
  const { selectedStudio } = useAppContext();

  const [currentEmployee, setCurrentEmployee] = useState<number | undefined>(
    selectedEmployeeId
  );
  const [selectedDateState, setSelectedDateState] = useState<Date | undefined>(
    selectedDate
  );
  const [currentMonth, setCurrentMonth] = useState(() => {
    return selectedDate
      ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
      : new Date();
  });

  // Pending slot — staged (highlighted) but not yet committed to the form
  const [pendingSlot, setPendingSlot] = useState<ITimeSlot | null>(null);

  // Custom-time section (inline, no longer a separate tab)
  const [selectionMode, setSelectionMode] = useState<SelectionMode>("slots");
  const [customDateTime, setCustomDateTime] = useState<Date | null>(null);
  const [customConflictWarning, setCustomConflictWarning] = useState<string>("");

  // Track initial-mount auto-switch so user choices aren't overridden
  const hasAutoInitialized = useRef(false);

  const selectedEmployee = useMemo(
    () => employeesData.find((i) => i.id === currentEmployee),
    [employeesData, currentEmployee]
  );

  // Minutes the employee stays busy after a new booking ending at `endMinute` on `date`: the
  // service's buffer, cut at the end of the employee's working day or where a blocked time of
  // that day starts. Existing bookings carry their own buffer in `busyUntil`.
  const getBufferAfter = useCallback(
    (date: Date, endMinute: number): number =>
      getEffectiveBufferMinutes(
        endMinute,
        bufferAfterMinutes,
        toWorkingShifts(
          selectedEmployee?.workingDays.filter((wd) => wd.dayOfWeek === date.getDay()) ?? []
        ),
        toBlockedIntervals(selectedEmployee?.bookedSlots ?? [], date)
      ),
    [bufferAfterMinutes, selectedEmployee]
  );

  // Generate calendar days for the current month view
  const calendarDays = useMemo(() => {
    const days: ICalendarDay[] = [];
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const dayOfWeek = firstDay.getDay();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - mondayOffset);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOfCurrentMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      1
    );

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);

      const isCurrentMonth = date.getMonth() === month;
      const isPastDate = date < today;
      const isSelected = Boolean(
        selectedDateState &&
          typeof selectedDateState.toDateString === "function" &&
          selectedDateState.toDateString() === date.toDateString()
      );
      const isToday = date.toDateString() === today.toDateString();
      const isWithinBookingRange = date >= startOfCurrentMonth;

      let availability: "free" | "busy" | "closed" = "closed";

      if (
        selectedEmployee &&
        ((isCurrentMonth && !isPastDate) ||
          (isWithinBookingRange && date < today))
      ) {
        const dayOfWeek = date.getDay();
        const hasEmployeeWorkingHours = selectedEmployee.workingDays.some(
          (wd) => wd.dayOfWeek === dayOfWeek
        );

        if (hasEmployeeWorkingHours) {
          const rangesForDate = getBookingRangesOnDate(
            selectedEmployee.bookedSlots,
            date
          );

          const workingHoursForDay = selectedEmployee.workingDays.filter(
            (wd) => wd.dayOfWeek === dayOfWeek
          );

          const slotDurationMinutes = selectedStudio?.timeStep ?? 30;
          let totalAvailableSlots = 0;
          let conflictingSlots = 0;

          workingHoursForDay.forEach((workingDay) => {
            const startTotalMinutes = timeToMinutes(workingDay.startTime);
            const endTotalMinutes = timeToMinutes(workingDay.endTime);

            for (
              let minute = startTotalMinutes;
              minute < endTotalMinutes;
              minute += slotDurationMinutes
            ) {
              totalAvailableSlots++;
              const slotEnd = minute + slotDurationMinutes;

              const hasConflict = rangesForDate.some(
                ({ start, end }) => minute < end && slotEnd > start
              );

              if (hasConflict) {
                conflictingSlots++;
              }
            }
          });

          availability =
            conflictingSlots >= totalAvailableSlots && totalAvailableSlots > 0
              ? "busy"
              : "free";
        }
      }

      days.push({
        date,
        dayNumber: date.getDate(),
        isCurrentMonth: Boolean(date.getMonth() === month),
        isSelected,
        isToday,
        availability,
      });
    }

    return days;
  }, [
    currentMonth,
    selectedEmployee,
    selectedDateState,
    selectedStudio?.timeStep,
  ]);

  const timeSlots = useMemo(() => {
    if (!selectedDateState || !selectedEmployee) return [];

    const slots: ITimeSlot[] = [];
    const dayOfWeek = selectedDateState.getDay();
    const employeeWorkingDaysForDate = selectedEmployee.workingDays.filter(
      (wd) => wd.dayOfWeek === dayOfWeek
    );

    if (employeeWorkingDaysForDate.length === 0) return [];

    const slotDurationMinutes = selectedStudio?.timeStep ?? 30;
    const bookingRangesForDate = getBookingRangesOnDate(
      selectedEmployee.bookedSlots,
      selectedDateState
    );

    const uniqueWorkingDays = employeeWorkingDaysForDate.filter(
      (workingDay, index, array) =>
        array.findIndex(
          (wd) =>
            wd.startTime === workingDay.startTime &&
            wd.endTime === workingDay.endTime
        ) === index
    );

    uniqueWorkingDays.forEach((workingDay) => {
      const startTotalMinutes = timeToMinutes(workingDay.startTime);
      const endTotalMinutes = timeToMinutes(workingDay.endTime);

      for (
        let minute = startTotalMinutes;
        minute < endTotalMinutes;
        minute += slotDurationMinutes
      ) {
        const hour = Math.floor(minute / 60);
        const min = minute % 60;
        const timeString = `${hour.toString().padStart(2, "0")}:${min
          .toString()
          .padStart(2, "0")}`;

        const bookingDuration = selectedDuration || slotDurationMinutes;
        const bookingStart = minute;
        const bookingBuffer = getBufferAfter(
          selectedDateState,
          minute + bookingDuration
        );
        const bookingEnd = minute + bookingDuration + bookingBuffer;

        const allConflicts = bookingRangesForDate
          .filter(({ start, end }) => bookingStart < end && bookingEnd > start)
          .map(({ booking }) => booking);

        const slotEnd = minute + slotDurationMinutes;
        const immediateConflict = bookingRangesForDate.find(
          ({ start, end }) => minute < end && slotEnd > start
        )?.booking;

        let status: "available" | "busy" | "outside-hours" = "available";
        let tooltip: React.ReactNode;

        if (immediateConflict) {
          status = "busy";
          tooltip = generateTooltipForBusySlot();
        } else if (allConflicts.length > 0) {
          tooltip = generateTooltipForConflicts(
            allConflicts,
            bookingDuration,
            timeString,
            bookingBuffer
          );
        } else {
          tooltip = `Свободен слот за резервация`;
        }

        // Highlighted if this slot is the currently pending one, or if (no pending) it matches the already-committed selection
        const matchesCommitted =
          selectedDate &&
          selectedDateState &&
          typeof selectedDateState.toDateString === "function" &&
          selectedDate.getHours() === hour &&
          selectedDate.getMinutes() === min &&
          selectedDateState.toDateString() === selectedDate.toDateString();

        const matchesPending =
          pendingSlot !== null &&
          pendingSlot.time === timeString &&
          selectionMode === "slots";

        const isSlotSelected = matchesPending || (!pendingSlot && matchesCommitted);

        const existingSlotIndex = slots.findIndex(
          (slot) => slot.time === timeString
        );

        if (existingSlotIndex === -1) {
          slots.push({
            time: timeString,
            status,
            tooltip,
            conflictInfo: immediateConflict,
            conflicts: allConflicts,
            isSelected: isSlotSelected,
          });
        } else {
          const existingSlot = slots[existingSlotIndex];
          const finalStatus =
            status === "busy" || existingSlot.status === "busy"
              ? "busy"
              : "available";

          const combinedConflicts = [
            ...existingSlot.conflicts,
            ...allConflicts,
          ];
          const uniqueConflicts = combinedConflicts.filter(
            (conflict, index, array) =>
              array.findIndex(
                (c) =>
                  c.startTime === conflict.startTime &&
                  c.endTime === conflict.endTime
              ) === index
          );

          let finalTooltip = tooltip;
          if (finalStatus === "busy" && immediateConflict) {
            finalTooltip = generateTooltipForBusySlot();
          } else if (
            finalStatus === "available" &&
            uniqueConflicts.length > 0
          ) {
            finalTooltip = generateTooltipForConflicts(
              uniqueConflicts,
              bookingDuration,
              timeString,
              bookingBuffer
            );
          }

          slots[existingSlotIndex] = {
            ...existingSlot,
            status: finalStatus,
            conflicts: uniqueConflicts,
            tooltip: finalTooltip,
            isSelected: isSlotSelected || existingSlot.isSelected,
          };
        }
      }
    });

    return slots.sort((a, b) => a.time.localeCompare(b.time));
  }, [
    selectedDateState,
    selectedEmployee,
    selectedStudio?.timeStep,
    selectedDate,
    selectedDuration,
    getBufferAfter,
    pendingSlot,
    selectionMode,
  ]);

  const selectedDateText = useMemo(() => {
    if (!selectedDateState) return "Изберете дата";
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    } as const;
    return selectedDateState.toLocaleDateString("bg-BG", options);
  }, [selectedDateState]);

  const monthYearText = useMemo(() => {
    const options = { month: "long", year: "numeric" } as const;
    return currentMonth.toLocaleDateString("bg-BG", options);
  }, [currentMonth]);

  // Groups slots by part-of-day: morning (<12), afternoon (12-17), evening (>=17)
  const groupedSlots = useMemo(() => {
    const morning: ITimeSlot[] = [];
    const afternoon: ITimeSlot[] = [];
    const evening: ITimeSlot[] = [];

    timeSlots.forEach((slot) => {
      const hour = parseInt(slot.time.split(":")[0], 10);
      if (hour < 12) morning.push(slot);
      else if (hour < 17) afternoon.push(slot);
      else evening.push(slot);
    });

    return { morning, afternoon, evening };
  }, [timeSlots]);

  const handleEmployeeChange = useCallback(
    (employeeId: number) => {
      setCurrentEmployee(employeeId);
      setValue(employeeFieldName, employeeId);
      // Reset any pending selection when employee changes
      setPendingSlot(null);
    },
    [employeeFieldName, setValue]
  );

  const handleDateSelect = useCallback((date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOfCurrentMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      1
    );

    if (date < startOfCurrentMonth) return;
    setSelectedDateState(date);
    // Date change invalidates any pending slot on the previous date
    setPendingSlot(null);
  }, []);

  // Stage a slot (no longer commits immediately — user confirms via footer)
  const handleTimeSlotSelect = useCallback(
    (slot: ITimeSlot) => {
      if (slot.status === "outside-hours") return;
      if (!selectedDateState || !currentEmployee) return;
      setPendingSlot(slot);
      // Any pending slot cancels a custom-time draft
      setCustomDateTime(null);
      setCustomConflictWarning("");
      setSelectionMode("slots");
    },
    [selectedDateState, currentEmployee]
  );

  const navigateMonth = useCallback((direction: "prev" | "next") => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() + (direction === "prev" ? -1 : 1));
      return newMonth;
    });
  }, []);

  const canNavigatePrev = useMemo(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonthNum = currentDate.getMonth();
    const calendarYear = currentMonth.getFullYear();
    const calendarMonthNum = currentMonth.getMonth();

    return (
      calendarYear > currentYear ||
      (calendarYear === currentYear && calendarMonthNum >= currentMonthNum)
    );
  }, [currentMonth]);

  // Custom-time section handlers
  const handleCustomDateTimeChange = useCallback(
    (selectedDateTime: Date | null) => {
      setCustomDateTime(selectedDateTime);
      setCustomConflictWarning("");
      // Custom-time takes precedence over any pending slot selection
      setPendingSlot(null);

      if (!selectedDateTime || !selectedEmployee || !selectedDuration) {
        return;
      }

      const bookingStartMinutes =
        selectedDateTime.getHours() * 60 + selectedDateTime.getMinutes();
      const bookingBuffer = getBufferAfter(
        selectedDateTime,
        bookingStartMinutes + selectedDuration
      );
      const bookingEndMinutes = bookingStartMinutes + selectedDuration + bookingBuffer;

      const conflicts = getBookingRangesOnDate(
        selectedEmployee.bookedSlots,
        selectedDateTime
      )
        .filter(
          ({ start, end }) =>
            bookingStartMinutes < end && bookingEndMinutes > start
        )
        .map(({ booking }) => booking);

      if (conflicts.length > 0) {
        setCustomConflictWarning(
          buildCustomConflictWarning(conflicts, selectedDuration, bookingBuffer)
        );
      }
    },
    [selectedEmployee, selectedDuration, getBufferAfter]
  );

  const openCustomMode = useCallback(() => {
    setSelectionMode("custom");
    // Clear pending slot selection so the pill-style highlight in the slot grid is released
    setPendingSlot(null);
    // Prefill with selected calendar date @ 12:00 if nothing chosen yet
    if (!customDateTime) {
      const base = selectedDateState ? new Date(selectedDateState) : new Date();
      base.setHours(12, 0, 0, 0);
      setCustomDateTime(base);
    }
  }, [customDateTime, selectedDateState]);

  const closeCustomMode = useCallback(() => {
    setSelectionMode("slots");
    setCustomDateTime(null);
    setCustomConflictWarning("");
  }, []);

  // One-shot auto-initialization: if the existing selection isn't on a standard slot,
  // open the custom section (preserves old auto-switch behavior for edit-mode)
  useEffect(() => {
    if (hasAutoInitialized.current) return;
    if (!selectedDate || !selectedEmployeeId) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const selectedDateOnly = createDateOnly(selectedDate);

    // If selected date is before current-month start, navigate calendar to that month
    // so user can still see the committed selection.
    if (selectedDateOnly < startOfCurrentMonth) {
      setCurrentMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
      setSelectionMode("custom");
      setCustomDateTime(selectedDate);
      hasAutoInitialized.current = true;
      return;
    }

    // Need timeSlots loaded to decide
    if (timeSlots.length === 0) return;

    const selectedTimeString = `${selectedDate.getHours().toString().padStart(2, "0")}:${selectedDate
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;

    const isInSlots = timeSlots.some((s) => s.time === selectedTimeString);
    if (!isInSlots) {
      setSelectionMode("custom");
      setCustomDateTime(selectedDate);
    }
    hasAutoInitialized.current = true;
  }, [selectedDate, selectedEmployeeId, timeSlots]);

  // Final commit — single exit point. Handles busy-confirm + duration-overflow confirm
  // for staged slot selections, and commits custom-time selection.
  const handleConfirm = useCallback(() => {
    if (!currentEmployee) return;

    // Custom-time commit
    if (selectionMode === "custom") {
      if (!customDateTime) return;
      setValue(dateFieldName, customDateTime);
      setValue(employeeFieldName, currentEmployee);
      if (!selectedDuration) {
        setValue(durationFieldName, selectedStudio?.timeStep || 60);
      }
      setCustomConflictWarning("");
      onClose();
      return;
    }

    // Slot commit
    if (!pendingSlot || !selectedDateState) return;

    const [hour, minute] = pendingSlot.time.split(":").map(Number);
    const committedDateTime = new Date(selectedDateState);
    committedDateTime.setHours(hour, minute, 0, 0);

    const commit = () => {
      setValue(dateFieldName, committedDateTime);
      setValue(employeeFieldName, currentEmployee);
      if (!selectedDuration) {
        setValue(durationFieldName, selectedStudio?.timeStep || 60);
      }
      onClose();
    };

    // Busy slot: confirm before committing
    if (pendingSlot.status === "busy") {
      showCustomConfirmDialog(
        `${pendingSlot.tooltip}<br/><br/>Сигурни ли сте, че искате да направите резервация в този слот?`,
        commit,
        undefined,
        "warning",
        "Да",
        "Отказ"
      );
      return;
    }

    // Duration overflow: confirm if service is longer than free window
    if (selectedDuration && pendingSlot.status === "available") {
      const slotDurationMinutes = selectedStudio?.timeStep ?? 30;
      const bookingBuffer = getBufferAfter(
        committedDateTime,
        hour * 60 + minute + selectedDuration
      );
      const serviceDurationMinutes = selectedDuration + bookingBuffer;

      if (serviceDurationMinutes > slotDurationMinutes) {
        const currentSlotIndex = timeSlots.findIndex(
          (s) => s.time === pendingSlot.time
        );
        const slotsNeeded = Math.ceil(
          serviceDurationMinutes / slotDurationMinutes
        );

        let hasConflict = false;
        for (let i = 1; i < slotsNeeded; i++) {
          const nextSlot = timeSlots[currentSlotIndex + i];
          if (!nextSlot || nextSlot.status !== "available") {
            hasConflict = true;
            break;
          }
        }

        if (hasConflict) {
          showCustomConfirmDialog(
            `Избраната услуга е с продължителност ${describeBooking(selectedDuration, bookingBuffer)}, а свободното време до следващия зает слот (или край на работното време) не е достатъчно. Искате ли да продължите?`,
            commit,
            undefined,
            "warning",
            "Да",
            "Не"
          );
          return;
        }
      }
    }

    commit();
  }, [
    currentEmployee,
    selectionMode,
    customDateTime,
    dateFieldName,
    employeeFieldName,
    durationFieldName,
    selectedDuration,
    selectedStudio?.timeStep,
    onClose,
    pendingSlot,
    selectedDateState,
    setValue,
    timeSlots,
    getBufferAfter,
  ]);

  // Summary strings for the sticky footer
  const summary = useMemo(() => {
    const employeeName = selectedEmployee?.name;

    let dateTime: { date: string; time: string } | null = null;

    if (selectionMode === "custom" && customDateTime) {
      const dateStr = customDateTime.toLocaleDateString("bg-BG", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
      const timeStr = `${customDateTime.getHours().toString().padStart(2, "0")}:${customDateTime
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;
      dateTime = { date: dateStr, time: timeStr };
    } else if (pendingSlot && selectedDateState) {
      const dateStr = selectedDateState.toLocaleDateString("bg-BG", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
      dateTime = { date: dateStr, time: pendingSlot.time };
    } else if (selectedDate && selectedDateState) {
      // Already-committed value
      const dateStr = selectedDateState.toLocaleDateString("bg-BG", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
      const timeStr = `${selectedDate.getHours().toString().padStart(2, "0")}:${selectedDate
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;
      dateTime = { date: dateStr, time: timeStr };
    } else if (selectedDateState) {
      const dateStr = selectedDateState.toLocaleDateString("bg-BG", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
      dateTime = { date: dateStr, time: "" };
    }

    return { employeeName, dateTime };
  }, [
    selectedEmployee,
    selectionMode,
    customDateTime,
    pendingSlot,
    selectedDateState,
    selectedDate,
  ]);

  const canConfirm = useMemo(() => {
    if (!currentEmployee) return false;
    if (selectionMode === "custom") return !!customDateTime;
    // Allow confirming if a pending slot is staged OR if an already-committed value exists
    if (pendingSlot) return true;
    return !!selectedDate;
  }, [currentEmployee, selectionMode, customDateTime, pendingSlot, selectedDate]);

  return {
    // State
    currentEmployee,
    selectedDateState,
    currentMonth,
    selectedEmployee,
    calendarDays,
    timeSlots,
    groupedSlots,
    selectedDateText,
    monthYearText,
    canNavigatePrev,

    // Custom-time section
    selectionMode,
    customDateTime,
    customConflictWarning,
    openCustomMode,
    closeCustomMode,
    handleCustomDateTimeChange,

    // Pending slot
    pendingSlot,

    // Summary + confirm
    summary,
    canConfirm,
    handleConfirm,

    // Handlers
    handleEmployeeChange,
    handleDateSelect,
    handleTimeSlotSelect,
    navigateMonth,
  };
};
